import { mediaQueryLarge, requestIdleCallback, prefersReducedMotion } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 * Uses a single DOM tree for both grid and list layouts.
 */
export default class ResultsList extends PaginatedList {
  #isFirstRender = true;
  #prefersReducedMotion = false;
  #currentTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#prefersReducedMotion = prefersReducedMotion();

    this.#restoreLayoutPreference();
    this.#grid?.classList.add('has-finished');

    requestIdleCallback(() => {
      this.#isFirstRender = false;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    if (this.#currentTimeout) {
      clearTimeout(this.#currentTimeout);
    }
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#switchLayout(target.value);
  }

  get #grid() {
    const { grid } = this.refs;
    return grid instanceof HTMLElement ? grid : null;
  }

  #getViewportKey(matches = mediaQueryLarge.matches) {
    return matches ? 'desktop' : 'mobile';
  }

  #normalizeLayout(value) {
    return value === 'list' ? 'list' : value || 'default';
  }

  #getStoredLayout(matches = mediaQueryLarge.matches) {
    return this.#normalizeLayout(sessionStorage.getItem(`product-grid-view-${this.#getViewportKey(matches)}`));
  }

  #syncControls(value) {
    const normalizedValue = this.#normalizeLayout(value);

    this.querySelectorAll('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.checked = input.value === normalizedValue;
    });
  }

  #restoreLayoutPreference() {
    const storedLayout = this.#getStoredLayout();
    this.#syncControls(storedLayout);
    this.#setLayout(storedLayout);
  }

  #switchLayout(value) {
    const grid = this.#grid;
    if (!grid) return;

    const normalizedValue = this.#normalizeLayout(value);
    this.#syncControls(normalizedValue);

    if (this.#currentTimeout) {
      clearTimeout(this.#currentTimeout);
    }

    if (this.#isFirstRender || this.#prefersReducedMotion) {
      this.#cleanupAnimations(grid);
      this.#setLayout(normalizedValue);
      this.#saveLayoutPreference(normalizedValue);
      return;
    }

    this.#cleanupAnimations(grid);
    grid.classList.add('is-animating', 'is-fading-out');

    this.#currentTimeout = window.setTimeout(() => {
      this.#setLayout(normalizedValue);
      grid.offsetHeight;
      grid.classList.remove('is-fading-out');
      grid.classList.add('is-entering');

      requestAnimationFrame(() => {
        grid.classList.add('is-visible');
      });

      this.#currentTimeout = window.setTimeout(() => {
        this.#cleanupAnimations(grid);
        grid.classList.add('has-finished');
      }, 200);
    }, 120);

    this.#saveLayoutPreference(normalizedValue);
  }

  #cleanupAnimations(grid = this.#grid) {
    if (!(grid instanceof HTMLElement)) return;

    grid.classList.remove('is-animating', 'is-fading-out', 'is-entering', 'is-visible', 'has-finished');
  }

  #saveLayoutPreference(value) {
    const normalizedValue = this.#normalizeLayout(value);

    requestIdleCallback(() => {
      sessionStorage.setItem(`product-grid-view-${this.#getViewportKey()}`, normalizedValue);
    });
  }

  #setLayout(value) {
    const grid = this.#grid;
    if (!grid) return;

    const normalizedValue = this.#normalizeLayout(value);
    grid.setAttribute('product-grid-view', normalizedValue);
    grid.dataset.resultsView = normalizedValue === 'list' ? 'list' : 'grid';
    grid.classList.add('is-active');
  }

  #handleMediaQueryChange = (event) => {
    const storedLayout = this.#getStoredLayout(event.matches);
    this.#syncControls(storedLayout);
    this.#cleanupAnimations();
    this.#setLayout(storedLayout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}